import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Button, Flex, Image, Text } from '@chakra-ui/react';
import * as ProcoreIframeHelpers from '@procore/procore-iframe-helpers';
import PropTypes from 'prop-types';

import PulseLoader from '../../../components/PulseLoader';
import PINSLogo from '../../../PINS_logo_197x79.png';
import { createErrorToast } from '../../../redux/modules/app';
import { setProcoreSidePanelProcoreUser } from '../../../redux/modules/procoreSidePanel';
import { useCompanyIdFromProcoreCompanyId } from '../../../services/company-service';
import underConstructionImage from '../../../under-construction.png';

const ProcoreOauthButton = ({ procoreCompanyId, procoreView, procoreResourceId, procoreProjectId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { data: pinsCompanyId, isLoading: companyIdIsLoading } = useCompanyIdFromProcoreCompanyId(procoreCompanyId);
  const procoreIframeHelper = ProcoreIframeHelpers.initialize();

  const procoreCallbackUrl = useMemo(() => {
    if (companyIdIsLoading) {
      return null;
    }

    const procorePrefix = PINS_ENV === 'prod' ? 'login' : 'sandbox';
    const clientId = PINS_ENV === 'prod' ? PROCORE_CLIENT_ID : PROCORE_SANDBOX_CLIENT_ID;

    const baseRedirectUrl = window.location.origin;

    return `https://${procorePrefix}.procore.com/oauth/authorize?client_id=${clientId}&response_type=code&redirect_uri=${baseRedirectUrl}/v2/side-panels/procore/oauth/callback?company_ids=${pinsCompanyId}.${procoreCompanyId}`;
  }, [PINS_ENV, pinsCompanyId, procoreCompanyId, companyIdIsLoading]);

  if (companyIdIsLoading) {
    return (
      <Box
        bg="background.main"
        height="100%"
        minHeight="100vh"
        padding={4}
      >
        <Text>Fetching PINS Company Id...</Text>
        <PulseLoader loading={companyIdIsLoading} />
      </Box>
    );
  }

  if (!procoreCompanyId || !pinsCompanyId || !procoreProjectId || !procoreView || !procoreCallbackUrl) {
    return (
      <Box
        bg="background.main"
        height="100%"
        minHeight="100vh"
        padding={4}
      >
        <Flex
          alignItems="center"
          flexDirection="column"
          height="100%"
          justifyContent="center"
          my={8}
          py={4}
        >
          <Image mb={8} src={underConstructionImage} />
          <Text
            fontSize="24px"
            fontWeight="500"
            letterSpacing="-0.02em"
            lineHeight="32px"
            my={4}
          >
            Uh oh...
          </Text>
          <Text my={2}>Looks like we are missing some verifications from Procore.</Text>
          <Text my={2}>Are you sure you are visiting this app from Procore&apos;s website?</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box
      bg="background.main"
      height="100%"
      minHeight="100vh"
      padding={4}
    >
      <Flex
        alignItems="center"
        flexDirection="column"
        height="100%"
        justifyContent="center"
        my={8}
        py={4}
      >
        <Image mb={8} src={PINSLogo} />
      </Flex>
      <Box mb={4}>
        <Text
          fontSize="24px"
          fontWeight="500"
          letterSpacing="-0.02em"
          lineHeight="32px"
          mb={4}
        >
          PINS Side Panel Application
        </Text>
        <Text>Click the button below to authenticate with Procore.</Text>
      </Box>

      <Box
        flexFlow="row wrap"
        justifyContent="center"
        textAlign="center"
      >
        <Button
          onClick={() => procoreIframeHelper.authentication.authenticate({
            url: procoreCallbackUrl,
            width: '700px',
            height: '700px',
            onSuccess(payload) {
              dispatch(setProcoreSidePanelProcoreUser({
                procoreUser: payload.procoreUser,
                procoreAccessToken: payload.procoreAccessToken,
                pinsUser: payload.pinsUser,
                pinsAccessToken: payload.pinsAccessToken
              }));

              // TODO: if we create more than one side-panel, here's where we look at procoreView to decide which slug to use
              history.replace('/side-panels/procore/project-records');
            },
            onFailure(error) {
              dispatch(createErrorToast({
                title: 'Procore Authentication Failed',
                description: error?.message,
                error
              }));

              console.log(error);
            }
          })}
        >
          Authenticate with Procore
        </Button>
      </Box>

    </Box>
  );
};

ProcoreOauthButton.propTypes = {
  procoreCompanyId: PropTypes.number.isRequired,
  procoreProjectId: PropTypes.number.isRequired,
  procoreResourceId: PropTypes.number.isRequired,
  procoreView: PropTypes.string.isRequired
};

export default ProcoreOauthButton;
